/**
 * Dynamic Report Template Engine v2.3.0
 * Generates report structures based on device type configuration
 */

import { DeviceTypeConfig } from '../types';

export interface ReportSection {
  title: string;
  titleAr: string;
  metrics: string[];
  chartType?: 'line' | 'bar' | 'gauge' | 'status';
  aggregation?: 'avg' | 'sum' | 'min' | 'max' | 'last';
}

export interface ReportTemplate {
  deviceType: string;
  sections: ReportSection[];
  defaultPeriod: '1h' | '6h' | '24h' | '7d' | '30d';
  chartConfig: any;
}

/**
 * Generate report template from device type configuration
 */
export function generateReportTemplate(deviceTypeConfig: DeviceTypeConfig): ReportTemplate {
  const sections: ReportSection[] = [];

  // Section 1: Summary Statistics
  sections.push({
    title: 'Summary Statistics',
    titleAr: 'الإحصائيات الملخصة',
    metrics: deviceTypeConfig.defaultMetrics,
    aggregation: 'avg',
  });

  // Section 2: Charts (based on chart type)
  if (deviceTypeConfig.chartType === 'gauge' && deviceTypeConfig.supportsLevel) {
    sections.push({
      title: 'Level Trends',
      titleAr: 'اتجاهات المستوى',
      metrics: ['level_cm'],
      chartType: 'line',
    });
  }

  if (deviceTypeConfig.supportsTemperature || deviceTypeConfig.supportsHumidity) {
    const metrics: string[] = [];
    if (deviceTypeConfig.supportsTemperature) metrics.push('temperature_c');
    if (deviceTypeConfig.supportsHumidity) metrics.push('humidity_rh');

    sections.push({
      title: 'Environmental Conditions',
      titleAr: 'الظروف البيئية',
      metrics,
      chartType: 'line',
    });
  }

  // Section 3: Battery/Signal (if supported)
  if (deviceTypeConfig.supportsBattery || deviceTypeConfig.supportsRssi) {
    const metrics: string[] = [];
    if (deviceTypeConfig.supportsBattery) metrics.push('battery_v');
    if (deviceTypeConfig.supportsRssi) metrics.push('rssi_dbm');

    sections.push({
      title: 'Device Health',
      titleAr: 'صحة الجهاز',
      metrics,
      chartType: 'line',
      aggregation: 'last',
    });
  }

  // Section 4: Alarms (if any)
  sections.push({
    title: 'Alarm Events',
    titleAr: 'أحداث التنبيهات',
    metrics: [],
    chartType: 'status',
  });

  return {
    deviceType: deviceTypeConfig.deviceType,
    sections,
    defaultPeriod: deviceTypeConfig.chartType === 'status' ? '24h' : '7d',
    chartConfig: {
      colorPrimary: deviceTypeConfig.colorPrimary,
      colorSecondary: deviceTypeConfig.colorSecondary,
    },
  };
}

/**
 * Format metric value with appropriate unit and precision
 */
export function formatMetricValue(metricName: string, value: number): string {
  const formatters: Record<string, (v: number) => string> = {
    temperature_c: (v) => `${v.toFixed(1)}°C`,
    humidity_rh: (v) => `${v.toFixed(1)}%`,
    level_cm: (v) => `${v.toFixed(1)} cm`,
    battery_v: (v) => `${v.toFixed(2)} V`,
    rssi_dbm: (v) => `${v.toFixed(0)} dBm`,
    power_w: (v) => `${v.toFixed(0)} W`,
    energy_kwh: (v) => `${v.toFixed(2)} kWh`,
    voltage_v: (v) => `${v.toFixed(1)} V`,
    current_a: (v) => `${v.toFixed(2)} A`,
  };

  const formatter = formatters[metricName];
  return formatter ? formatter(value) : value.toFixed(2);
}

/**
 * Get metric label in English or Arabic
 */
export function getMetricLabel(metricName: string, isArabic: boolean): string {
  const labels: Record<string, { en: string; ar: string }> = {
    temperature_c: { en: 'Temperature', ar: 'الحرارة' },
    humidity_rh: { en: 'Humidity', ar: 'الرطوبة' },
    level_cm: { en: 'Level', ar: 'المستوى' },
    battery_v: { en: 'Battery', ar: 'البطارية' },
    rssi_dbm: { en: 'Signal Strength', ar: 'قوة الإشارة' },
    power_w: { en: 'Power', ar: 'القدرة' },
    energy_kwh: { en: 'Energy', ar: 'الطاقة' },
    voltage_v: { en: 'Voltage', ar: 'الجهد' },
    current_a: { en: 'Current', ar: 'التيار' },
    door_state: { en: 'Door Status', ar: 'حالة الباب' },
    motion_detected: { en: 'Motion', ar: 'الحركة' },
  };

  const label = labels[metricName];
  return label ? (isArabic ? label.ar : label.en) : metricName;
}

/**
 * Generate report data structure
 */
export async function generateReportData(
  deviceId: string,
  template: ReportTemplate,
  telemetryData: any[],
  alarmEvents: any[],
): Promise<any> {
  const reportData: any = {
    deviceId,
    deviceType: template.deviceType,
    period: template.defaultPeriod,
    generatedAt: new Date().toISOString(),
    sections: [],
  };

  for (const section of template.sections) {
    const sectionData: any = {
      title: section.title,
      titleAr: section.titleAr,
      data: [],
    };

    if (section.metrics.length > 0) {
      // Calculate aggregated values
      for (const metric of section.metrics) {
        const values = telemetryData
          .map((d) => d[metric])
          .filter((v) => v !== null && v !== undefined);

        if (values.length > 0) {
          let aggregatedValue = 0;
          switch (section.aggregation) {
            case 'avg':
              aggregatedValue = values.reduce((a, b) => a + b, 0) / values.length;
              break;
            case 'sum':
              aggregatedValue = values.reduce((a, b) => a + b, 0);
              break;
            case 'min':
              aggregatedValue = Math.min(...values);
              break;
            case 'max':
              aggregatedValue = Math.max(...values);
              break;
            case 'last':
              aggregatedValue = values[values.length - 1];
              break;
            default:
              aggregatedValue = values.reduce((a, b) => a + b, 0) / values.length;
          }

          sectionData.data.push({
            metric,
            value: aggregatedValue,
            formattedValue: formatMetricValue(metric, aggregatedValue),
          });
        }
      }
    }

    // Add alarm data if applicable
    if (section.title.includes('Alarm')) {
      sectionData.alarms = alarmEvents;
    }

    reportData.sections.push(sectionData);
  }

  return reportData;
}
